import { useAuth } from '../auth/AuthContext';
import {
  homeFor, portalFor, portalLabel, PORTAL_ROOT, OWNER_ROOT, PROVIDER_ROOT,
} from '@shared/portal';

/**
 * Which places this visitor can go, and what they are called.
 *
 * The decision itself — which roles belong to which portal — moved to
 * packages/shared/src/portal.ts in Phase 0 of the mobile app, so the phone
 * and the browser can never send the same person to different places. What
 * stays here is the React hook that reads the session.
 *
 * The role list is a convenience for choosing a label, not a permission check.
 * The server decides what a platform role may actually do, on every request.
 */

export { homeFor, PORTAL_ROOT, OWNER_ROOT, PROVIDER_ROOT };

export interface Portal {
  isAuthenticated: boolean;
  /** Holds a Control Center role. */
  platform: boolean;
  /** Signs in as a tenant rather than as a landlord or staff member. */
  tenant: boolean;
  /** Signs in as a property owner rather than a landlord, tenant or staff. */
  owner: boolean;
  /** The marketing homepage. "/" belongs to the dashboard once signed in. */
  siteHome: string;
  /** The portal this person works in, or the sign-in page if they do not. */
  appHome: string;
  /** What to call it on a button. */
  appLabel: string;
}

export function usePortal(): Portal {
  const { isAuthenticated, roles } = useAuth();
  const kind = portalFor(roles);
  return {
    isAuthenticated,
    platform: kind === 'platform',
    tenant: kind === 'tenant',
    owner: kind === 'owner',
    siteHome: isAuthenticated ? '/home' : '/',
    appHome: !isAuthenticated ? '/login' : homeFor(roles),
    appLabel: portalLabel(kind),
  };
}
